import AppDialog from '@/common/components/Dialog.tsx'
import { Button } from '@/app/common/components/button/Button'
import ProviderFileUpload from '@/pages/providers/bulk_upload_providers/ProviderFileUpload.tsx'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ProviderQueries, ProviderQueryKeys } from '@/queries/providers/queries'
import { useSelector } from 'react-redux'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { useState } from 'react'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { selectSelectedProgram } from '@/storage/programs/slice.ts'
import axios from 'axios'

type ProviderImportModalProps = {
  importModalVisible: boolean
  setImportModalVisible: (value: boolean) => void
}

const ProviderImportModal = ({
  importModalVisible,
  setImportModalVisible,
}: ProviderImportModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const programId = useSelector(selectSelectedProgram).id
  const queryClient = useQueryClient()
  const { fireAlert } = useAppAlert()

  const downloadFailedProvidersFile = async (filePath: string) => {
    try {
      const response = await axios.get(`/download-file?path=${filePath}`, {
        responseType: 'blob',
      })

      const fileURL = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = fileURL
      link.download = 'failed_providers.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(fileURL)
    } catch (error) {
      await fireAlert({
        variant: 'error',
        titleText: 'Download Failed',
        message:
          error instanceof Error
            ? error.message
            : 'There was an error downloading the failed providers file.',
      })
    }
  }

  const mutation = useMutation(
    ProviderQueries.importProviders({
      onSuccess: async (response) => {
        if (response.total_failed_imported > 0) {
          await fireAlert({
            variant: 'error',
            titleText: 'Import Result',
            messageComponent: (
              <div className="text-sm space-y-4">
                <div className="space-y-1">
                  <p className="text-gray-800 font-semibold">
                    {response.total_successfully_imported}: providers were
                    imported successfully.
                  </p>
                  <p className="text-red-600 font-semibold">
                    {response.total_failed_imported}: providers failed to
                    import.
                  </p>
                </div>

                <div className="border-t border-gray-200 pt-2">
                  <p className="text-gray-700">
                    You can download the detailed error report:
                  </p>
                  {response.download_file && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        downloadFailedProvidersFile(response.download_file!)
                      }
                      className="mt-2"
                    >
                      Download Failed Providers (.csv)
                    </Button>
                  )}
                </div>
              </div>
            ),
          })
        } else {
          await fireAlert({
            variant: 'success',
            message: response.message,
          })
        }

        queryClient.invalidateQueries({ queryKey: ProviderQueryKeys.list_all })
        setImportModalVisible(false)
        setSelectedFile(null)
      },
      onError: async (error) => {
        await fireAlert({
          variant: 'error',
          message: error.message,
        })
      },
    })
  )
  const handleUpload = () => {
    if (selectedFile && programId) {
      mutation.mutate({ file: selectedFile, programId: programId })
    }
  }

  return (
    <>
      <AppDialog open={importModalVisible} setOpen={setImportModalVisible}>
        <AppDialog.Title>Bulk Upload Providers</AppDialog.Title>

        <div className="flex flex-col mt-6 space-y-6">
          <ProviderFileUpload
            onFileAccepted={(file) => setSelectedFile(file)}
          />

          <div className="bg-blue-50 text-sm text-gray-600 border border-blue-200 rounded-md p-4">
            <p>
              <strong>File Requirements:</strong>
            </p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>CSV or Excel format (.csv, .xlsx, .xls)</li>
              <li>Required columns: Name, Email, Category</li>
              <li>Optional columns: Phone, Organization</li>
              <li>Maximum 1000 providers per upload</li>
            </ul>
          </div>

          <div className="mt-8 flex items-center justify-end gap-x-2">
            <div>
              <Button
                size="xl"
                variant="outline"
                onClick={() => {
                  setImportModalVisible(false)
                  setSelectedFile(null)
                }}
                type="button"
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
            </div>

            <div>
              <SubmitButton
                status={mutation.status}
                onClick={handleUpload}
                buttonTxt="Upload Providers"
              />
            </div>
          </div>
        </div>
      </AppDialog>
    </>
  )
}

export default ProviderImportModal
